import type { DownloadedFile, HttpClient } from '@/api/httpClient';

import type { StockIntegrityReport, StockLookupItem, StockTrace } from './stock/stockTypes';

/**
 * Couche d'acces aux routes d'administration (`/api/v1/admin/**`).
 *
 * Pourquoi elle ne passe PAS par le `dataProvider` de React Admin, alors que les
 * ressources metier du catalogue (profils bebe, documents legaux) le font :
 *
 * 1. **L'enveloppe differe.** Les routes metier rendent `{items, hasMore, limit,
 *    offset}` — sans total, et c'est une regle testee du dossier : on n'invente
 *    pas un nombre que le serveur ne donne pas. Les routes d'administration
 *    rendent `{items, page, perPage, total}`, avec un `total` REEL issu d'un
 *    `COUNT(*)`. Faire cohabiter les deux dans un meme `dataProvider` obligerait
 *    a y brancher un discriminant par ressource, et la premiere erreur de
 *    branchement afficherait un total invente sur un ecran metier — exactement
 *    ce que la regle interdit.
 * 2. **Les actions ne sont pas du CRUD.** « Anonymiser un compte », « rejouer un
 *    message en echec », « basculer la maintenance » n'ont pas d'equivalent dans
 *    le vocabulaire de React Admin (`create`/`update`/`delete`). Les plier a
 *    `update` produirait des ecrans qui mentent sur ce qu'ils font.
 *
 * Le client HTTP reste le meme (`useHttpClient`) : rafraichissement proactif et
 * reactif du jeton, rejeu unique sur 401, message d'erreur traduit. Rien de tout
 * cela n'est redecrit ici.
 */

// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------

/**
 * Roles d'administration (ADR 0032).
 *
 * `null` n'est pas une valeur de cette union : l'absence de role est representee
 * par `null` au niveau du champ, jamais par une chaine vide ni par un role
 * « aucun ». Un role fantome dans l'union finirait tot ou tard compare a `>=`.
 */
export type AdminRole = 'support' | 'admin';

/** Hierarchie des roles. `admin` satisfait `support`, jamais l'inverse. */
const ROLE_RANK: Readonly<Record<AdminRole, number>> = { support: 1, admin: 2 };

export const roleSatisfies = (role: AdminRole | null, minimum: AdminRole): boolean =>
  role !== null && ROLE_RANK[role] >= ROLE_RANK[minimum];

export const ROLE_LABELS: Readonly<Record<AdminRole, string>> = {
  support: 'Support (lecture seule)',
  admin: 'Administrateur',
};

// ---------------------------------------------------------------------------
// Enveloppe de liste
// ---------------------------------------------------------------------------

export interface AdminPage<T> {
  readonly items: readonly T[];
  readonly page: number;
  readonly perPage: number;
  /** Total REEL, rendu par le serveur. Jamais calcule ni devine ici. */
  readonly total: number;
}

/**
 * Valide l'enveloppe sans supposer sa forme.
 *
 * Le JSON est une frontiere de donnees : un serveur qui repondrait autre chose
 * doit produire une erreur lisible, pas un `undefined` qui traverse trois
 * composants avant de casser un `.map`.
 */
const readPage = <T>(body: unknown, resource: string): AdminPage<T> => {
  if (typeof body !== 'object' || body === null) {
    throw new Error(`Réponse inattendue du serveur pour « ${resource} ».`);
  }

  const record = body as Record<string, unknown>;
  const items: unknown = record.items;

  if (!Array.isArray(items)) {
    throw new Error(`Réponse sans liste d’éléments pour « ${resource} ».`);
  }

  const numberOr = (value: unknown, fallback: number): number =>
    typeof value === 'number' && Number.isFinite(value) ? value : fallback;

  return {
    items: items as readonly T[],
    page: numberOr(record.page, 1),
    perPage: numberOr(record.perPage, items.length),
    // Pas de repli sur `items.length` : ce serait un total invente, et il
    // paraitrait juste tant qu'une seule page existe.
    total: numberOr(record.total, -1),
  };
};

// ---------------------------------------------------------------------------
// Formes rendues par l'API
// ---------------------------------------------------------------------------

export interface AdminIdentity {
  readonly userId: string;
  readonly email: string;
  readonly adminRole: AdminRole | null;
}

export interface AdminUserSummary {
  readonly id: string;
  readonly email: string;
  readonly emailVerifiedAt: string | null;
  readonly createdAt: string;
  readonly lastLoginAt: string | null;
  readonly adminRole: AdminRole | null;
  readonly anonymized: boolean;
  /** Profils crees par le compte, d'apres le journal de synchronisation ; `0` si anonymise. */
  readonly babyCount: number;
  /** Suspension en cours (BO-10) : instant et motif type, nuls ensemble. */
  readonly suspendedAt: string | null;
  readonly suspensionReasonCode: string | null;
  /** Appareils distincts parmi les sessions utilisables (BO-15). */
  readonly activeDeviceCount: number;
  /** Derniere mise a jour d'une session non revoquee ; `null` sans session ouverte. */
  readonly lastActivityAt: string | null;
}

/**
 * Filtres serveur de la liste des comptes (`GET /admin/users`, BO-15). Une cle
 * absente ou vide n'est pas envoyee : le serveur lit « pas de filtre ».
 */
export interface AdminUserFilters {
  readonly state?: string;
  readonly role?: string;
  readonly createdFrom?: string;
  readonly createdTo?: string;
  readonly babies?: string;
}

/**
 * Detenteur d'un role d'exploitation (`GET /admin/admins`, BO-13).
 *
 * `roleGrantedAt` vient de l'audit et vaut `null` quand le role a ete octroye
 * avant l'audit : l'ecran ecrit alors « — », il ne devine aucune date.
 */
export interface AdminHolder {
  readonly userId: string;
  readonly email: string;
  readonly adminRole: AdminRole;
  readonly lastLoginAt: string | null;
  readonly roleGrantedAt: string | null;
}

/**
 * Profil bebe vu depuis l'administration (`GET /admin/babies`, BO-17).
 *
 * Sans donnee de sante : le serveur ne rend ni date de naissance, ni sexe, ni
 * mesures, ni note. `ageMonths` est calcule par le serveur et affiche tel quel.
 */
export interface AdminFamilySummary {
  readonly id: string;
  readonly firstName: string;
  readonly ageMonths: number;
  readonly archived: boolean;
  readonly archivedAt: string | null;
  readonly memberCount: number;
  readonly pendingInvitationCount: number;
  readonly createdAt: string;
}

/** Compte actif rattache au profil ; `role` est le role de coparentalite. */
export interface AdminFamilyMember {
  readonly userId: string;
  readonly email: string;
  readonly role: string;
  readonly since: string;
}

/**
 * Invitation d'un profil. `email` est l'adresse d'un tiers, pas forcement d'un
 * compte. `status` est calcule a la lecture par le serveur (`expired`).
 */
export interface AdminFamilyInvitation {
  readonly id: string;
  readonly email: string;
  readonly role: string;
  readonly status: string;
  readonly invitedByEmail: string | null;
  readonly createdAt: string;
  readonly expiresAt: string;
}

/** Evenement du partage ; une adresse nulle est celle d'un compte anonymise. */
export interface AdminFamilyEvent {
  readonly id: string;
  readonly action: string;
  readonly occurredAt: string;
  readonly actorUserId: string;
  readonly actorEmail: string | null;
  readonly targetUserId: string | null;
  readonly targetEmail: string | null;
  readonly role: string | null;
}

export interface AdminFamilyDetail extends AdminFamilySummary {
  readonly owner: { readonly userId: string; readonly email: string } | null;
  readonly members: readonly AdminFamilyMember[];
  readonly invitations: readonly AdminFamilyInvitation[];
  /** Les 50 derniers evenements au plus, les plus recents d'abord. */
  readonly history: readonly AdminFamilyEvent[];
}

export type AdminFamilyState = 'active' | 'archived';

export interface AdminFamiliesQuery {
  readonly search: string;
  /** Absent : tous les profils. */
  readonly state: AdminFamilyState | null;
  readonly pendingInvitation: boolean;
}

/**
 * Profil bebe **cree** par le compte, lu dans le journal de synchronisation
 * (ADR 0035). Aucun role : le journal ne dit rien de la coparentalite.
 */
export interface AdminUserBaby {
  readonly id: string;
  readonly firstName: string | null;
  readonly createdAt: string;
}

/** Session d'un compte (`GET /admin/users/{id}/sessions`) : ni jeton, ni hash de jeton. */
export interface AdminUserSession {
  readonly id: string;
  readonly deviceHash: string | null;
  readonly createdAt: string;
  readonly lastActivityAt: string;
  readonly refreshTokenExpiresAt: string;
  readonly revokedAt: string | null;
  readonly active: boolean;
}

/** Activite de synchronisation d'un compte (`GET /admin/users/{id}/activity`) : des comptages seulement. */
export interface AdminUserActivity {
  /** Fuseau des jours de `days` (« UTC »), dit par le serveur. */
  readonly timezone: string;
  readonly days: readonly { readonly date: string; readonly operations: number }[];
  readonly byEntityType: readonly { readonly entityType: string; readonly count: number }[];
  readonly sync: {
    readonly lastPushAt: string | null;
    readonly acceptedInPeriod: number;
    /** Operations refusees pour conflit (journal d'idempotence) : detectees, pas resolues. */
    readonly conflictsInPeriod: number;
    /** Conflits detectes sur la periode, lus au registre et repartis par etat (ADR 0039). */
    readonly conflicts: AdminSyncConflictCounts;
  };
}

// ---------------------------------------------------------------------------
// Registre des conflits de synchronisation (ADR 0039, #635)
// ---------------------------------------------------------------------------

/**
 * Les quatre compteurs du registre : `detected` = `open` + `resolved` +
 * `notApplicable`. « Ouvert » comprend les conflits renvoyes a l'utilisatrice,
 * « resolu » ceux que l'administration a classes ; « sans objet » est a part.
 */
export interface AdminSyncConflictCounts {
  readonly detected: number;
  readonly open: number;
  readonly resolved: number;
  readonly notApplicable: number;
}

/**
 * Un conflit du registre, **en metadonnees seulement** : la version refusee et
 * la note d'un administrateur n'ont pas de champ ici, et le serveur ne les rend
 * pas. `versionAvailable` dit seulement qu'une version est encore conservee.
 */
export interface AdminSyncConflict {
  readonly id: string;
  readonly userId: string;
  readonly email: string;
  readonly entityType: string;
  readonly entityId: string;
  readonly classification: string;
  readonly status: string;
  readonly resolution: string | null;
  readonly reason: string;
  readonly baseVersion: number | null;
  readonly serverVersion: number;
  readonly detectionCount: number;
  readonly origin: string;
  readonly detectedAt: string;
  readonly lastDetectedAt: string;
  readonly resolvedAt: string | null;
  readonly versionAvailable: boolean;
}

export interface AdminSyncConflictTransition {
  readonly fromStatus: string | null;
  readonly toStatus: string;
  /** `appareil`, `admin` ou `systeme` : un genre, jamais un identifiant. */
  readonly actorKind: string;
  /** Code d'une liste fermee, jamais un texte saisi. */
  readonly reasonCode: string;
  readonly proofEventId: number | null;
  readonly abandonedKeyCount: number;
  readonly occurredAt: string;
}

export interface AdminSyncEntityEvent {
  readonly eventId: number;
  readonly version: number;
  readonly operationType: string;
  readonly byThisAccount: boolean;
  readonly deviceHash: string | null;
  readonly occurredAt: string;
}

export interface AdminSyncConflictDetail extends AdminSyncConflict {
  readonly operationType: string;
  /** Empreinte courte de l'appareil de la session : il se compare, il ne se lit pas. */
  readonly deviceHash: string | null;
  readonly appVersion: string | null;
  readonly payloadHash: string;
  readonly serverEventId: number | null;
  readonly parentConflictId: string | null;
  readonly resolvedByKind: string | null;
  readonly reasonCode: string | null;
  readonly referralExpiresAt: string | null;
  readonly decisionClaimedAt: string | null;
  readonly deviceAckAt: string | null;
  readonly closedAt: string | null;
  readonly versionPurgeAt: string;
  readonly transitions: readonly AdminSyncConflictTransition[];
  readonly entityEvents: readonly AdminSyncEntityEvent[];
  readonly entityEventsTruncated: boolean;
}

export interface AdminSyncConflictPage extends AdminPage<AdminSyncConflict> {
  /** Repartition par etat des conflits retenus par les autres filtres (l'etat est exclu). */
  readonly counts: AdminSyncConflictCounts;
}

/** Lit les compteurs sans en inventer : un serveur qui n'en rend pas produit une erreur lisible. */
const readConflictCounts = (value: unknown): AdminSyncConflictCounts => {
  const record = typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;
  const count = (key: string): number => {
    const raw = record?.[key];

    if (typeof raw !== 'number' || !Number.isInteger(raw) || raw < 0) {
      throw new Error('Réponse sans compteurs de conflits lisibles.');
    }

    return raw;
  };

  return { detected: count('detected'), open: count('open'), resolved: count('resolved'), notApplicable: count('notApplicable') };
};

export interface AdminConsent {
  readonly type: string;
  readonly granted: boolean;
  readonly policyVersion: string | null;
  /** Date d'octroi. Distincte de `updatedAt` : un consentement revoque garde la sienne. */
  readonly grantedAt: string | null;
  readonly revokedAt: string | null;
  readonly updatedAt: string | null;
}

export interface AdminDeletionRequest {
  readonly id: string;
  readonly status: string;
  readonly reasonCode: string | null;
  readonly requestedAt: string;
  readonly completedAt: string | null;
}

export interface AdminUserDetail extends AdminUserSummary {
  readonly babies: readonly AdminUserBaby[];
  readonly activeSessionCount: number;
  readonly consents: readonly AdminConsent[];
  readonly latestDeletionRequest: AdminDeletionRequest | null;
}

export interface AdminAuditEvent {
  readonly id: string;
  readonly action: string;
  readonly actorUserId: string | null;
  /**
   * Adresse **actuelle** du compte acteur, lue a la reponse et jamais ecrite
   * dans le journal : une adresse anonymisee reste anonymisee. `null` pour la
   * console ou un compte introuvable.
   */
  readonly actorEmail: string | null;
  readonly babyProfileId: string | null;
  readonly targetType: string | null;
  readonly targetId: string | null;
  /** Adresse actuelle du compte vise, seulement quand `targetType` vaut `identity_user`. */
  readonly targetEmail: string | null;
  readonly metadata: Readonly<Record<string, unknown>>;
  readonly correlationId: string | null;
  readonly occurredAt: string;
}

export interface AdminHealthCheck {
  readonly name: string;
  readonly status: 'ok' | 'degraded' | 'failed';
  readonly detail: string;
  readonly latencyMs: number | null;
}

export interface AdminHealth {
  readonly status: 'ok' | 'degraded' | 'failed';
  readonly checks: readonly AdminHealthCheck[];
}

/** Periodes de `GET /admin/metrics` ; absente, le serveur sert `7d`. */
export type MetricsPeriodKey = '24h' | '7d' | '30d';

export type EntryKind = 'bottle' | 'breastfeeding' | 'pumping' | 'solid_meal' | 'growth';

/**
 * Agregats de pilotage. Chaque nombre est rendu par le serveur ; `null` veut dire
 * « non mesurable honnetement », jamais zero (`accounts.active.previous`,
 * `series.activeAccounts`, `api.p50Ms` sans requete).
 */
export interface AdminMetrics {
  readonly period: { readonly key: MetricsPeriodKey; readonly from: string; readonly to: string };
  readonly accounts: {
    readonly active: { readonly current: number; readonly previous: number | null };
    readonly created: { readonly current: number; readonly previous: number; readonly verified: number };
  };
  readonly entries: {
    readonly current: number;
    readonly previous: number;
    readonly byKind: readonly { readonly kind: EntryKind; readonly count: number }[];
  };
  /** `conflicts` : operations rejetees pour conflit de version ; l'appareil a pu les renvoyer depuis. */
  readonly sync: { readonly received: number; readonly conflicts: number };
  readonly api: {
    readonly windowMinutes: number;
    readonly requests: number;
    readonly serverErrors: number;
    readonly p50Ms: number | null;
    readonly p95Ms: number | null;
  };
  readonly series: {
    readonly days: readonly string[];
    readonly activeAccounts: readonly number[] | null;
    readonly newAccounts: readonly number[];
    readonly entriesByKind: Readonly<Record<EntryKind, readonly number[]>>;
  };
}

/** Periodes de `GET /admin/statistics` ; absente, le serveur sert `90d`. Jamais de plage libre. */
export type StatisticsPeriodKey = '30d' | '90d';

/**
 * Un compteur d'usage passe par le seuil d'affichage du serveur. `suppressed` :
 * le groupe compte moins de `k` comptes, `value` est alors `null` et **l'ecran
 * n'ecrit aucun nombre**. Plancher d'affichage, pas une protection (#525).
 */
export interface ThresholdedCount {
  readonly value: number | null;
  readonly suppressed: boolean;
}

/**
 * Statistiques d'usage, version 1 : des compteurs globaux, sans croisement.
 *
 * Aucune serie historique de comptes actifs (le serveur ne peut pas la servir
 * honnetement) : `active` ne rend que les valeurs courantes, `weekly` que des
 * evenements dates. `inProgress` ne vaut vrai que pour la derniere semaine.
 */
export interface AdminStatistics {
  readonly period: StatisticsPeriodKey;
  readonly k: number;
  readonly timezone: string;
  readonly active: { readonly day: ThresholdedCount; readonly week: ThresholdedCount; readonly month: ThresholdedCount };
  readonly weekly: readonly {
    readonly weekStart: string;
    readonly inProgress: boolean;
    readonly newAccounts: ThresholdedCount;
    readonly entriesByKind: Readonly<Record<EntryKind, ThresholdedCount>>;
  }[];
}

export type AttentionSeverity = 'critical' | 'warning' | 'info';

export interface AdminAttentionItem {
  /** Cle stable de la source (`failed_messages`, `health`, `maintenance`...). */
  readonly kind: string;
  readonly severity: AttentionSeverity;
  readonly count: number;
  readonly since: string | null;
  readonly latest: { readonly label?: string; readonly at?: string } | null;
}

export interface AdminAttention {
  readonly generatedAt: string;
  /** Deja classes par le serveur : gravite decroissante, puis nombre. */
  readonly items: readonly AdminAttentionItem[];
  /** Sources qui n'ont pas pu repondre : « on ne sait pas », pas « rien a traiter ». */
  readonly unavailableSources: readonly string[];
}

export interface AdminFailedMessage {
  readonly id: string;
  readonly class: string;
  /** Nuls pour un message arrive dans le transport d'echec autrement que par un rejet. */
  readonly failedAt: string | null;
  readonly error: string | null;
}

export interface AdminSetting {
  readonly key: string;
  readonly label: string;
  readonly description: string;
  /** `instant` : chaine ISO 8601 en UTC, vide quand il est absent. */
  readonly type: 'boolean' | 'text' | 'instant';
  readonly value: boolean | string;
  readonly updatedAt: string | null;
  readonly updatedBy: string | null;
}

/**
 * Etat de la maintenance, **calcule par le serveur** (`GET /admin/maintenance`).
 *
 * `enabled` est le commutateur ; l'etat du service est `activeNow`. Les bornes
 * sont des instants ISO 8601 en UTC, nuls ensemble quand aucune fenetre n'est
 * programmee ; `nextChangeAt` est nul quand rien ne change plus (commutateur
 * allume, ou fenetre terminee).
 */
export interface AdminMaintenance {
  readonly enabled: boolean;
  readonly startsAt: string | null;
  readonly endsAt: string | null;
  readonly message: string;
  readonly activeNow: boolean;
  readonly nextChangeAt: string | null;
}

// ---------------------------------------------------------------------------
// Contenus legaux (versions, brouillon, publication ; BO-12)
// ---------------------------------------------------------------------------

/** Les deux seuls documents : un type de plus casserait l'ecran des versions mobiles installees. */
export type AdminLegalDocumentType = 'cgu' | 'cgv';

/** Compte d'administration auteur ou publicateur ; `email` est nul quand le compte n'existe plus. */
export interface AdminLegalActor {
  readonly id: string;
  readonly email: string | null;
}

/** Une version, **sans son texte**. Un brouillon n'a pas de numero : il le recoit a la publication. */
export interface AdminLegalVersionSummary {
  readonly number: number | null;
  readonly status: 'draft' | 'published';
  /** Compteur du brouillon ; c'est lui qu'on renvoie pour detecter deux editeurs. */
  readonly revision: number;
  /** Nul pour la version 1, issue de la migration. */
  readonly author: AdminLegalActor | null;
  /** Instant ou le texte a ete ecrit (dernier enregistrement d'un brouillon). */
  readonly savedAt: string;
  readonly publishedAt: string | null;
  readonly publishedBy: AdminLegalActor | null;
  readonly length: number;
  readonly sha256: string;
  readonly provisional: boolean;
}

export interface AdminLegalVersion extends AdminLegalVersionSummary {
  readonly content: string;
}

export interface AdminLegalDocument {
  readonly type: AdminLegalDocumentType;
  /** Numero que recevrait la prochaine publication, rendu par le serveur. */
  readonly nextNumber: number;
  readonly published: AdminLegalVersionSummary | null;
  readonly draft: AdminLegalVersionSummary | null;
}

// ---------------------------------------------------------------------------
// Explorateur de donnees (journal de synchronisation, ADR 0035 §1)
// ---------------------------------------------------------------------------

/** Compte qui a pousse une version. L'adresse est `null` quand le compte n'existe plus. */
export interface AdminDataActor {
  readonly id: string;
  readonly email: string | null;
}

export interface AdminCollectionSummary {
  /** Type d'enregistrement du journal (`feeding_session`…). Chaine : une valeur inconnue reste lisible. */
  readonly type: string;
  /** `COUNT(*)` lu en base ; inclut les suppressions logiques faites sur l'appareil. */
  readonly count: number;
  readonly lastUpdatedAt: string | null;
}

export interface AdminDataRecord {
  readonly entityType: string;
  readonly entityId: string;
  /** Compteur de version du SERVEUR (la creation vaut 0), distinct du `version` local du payload. */
  readonly version: number;
  /** Instant de reception par le serveur. */
  readonly updatedAt: string;
  readonly lastWriter: AdminDataActor;
  /** Charge utile non validee par le serveur : de forme libre, lue defensivement. */
  readonly payload: unknown;
  /** Chemins du texte libre retire pour ce role (`session.comment`). Vide pour `admin`. */
  readonly redactedFields: readonly string[];
}

export interface AdminRecordVersion {
  readonly eventId: number;
  readonly version: number;
  readonly operationType: string;
  /** Horloge de l'APPAREIL, pas celle du serveur. */
  readonly occurredAt: string;
  readonly originDeviceHash: string | null;
  readonly actor: AdminDataActor;
  readonly payload: unknown;
  readonly redactedFields: readonly string[];
}

/**
 * Operation **rejetee** par le serveur pour conflit de version. La ligne reste ecrite :
 * l'appareil a pu renvoyer l'operation depuis, sous une cle neuve, sans que rien ne
 * la marque resolue. Ce n'est donc pas un etat « a traiter ».
 */
export interface AdminVersionConflict {
  readonly createdAt: string;
  readonly operationType: string;
  readonly currentVersion: number | null;
}

export interface AdminRecordHistory extends AdminPage<AdminRecordVersion> {
  readonly conflicts: readonly AdminVersionConflict[];
  /** Vrai quand plus de cent conflits existent : seuls les cent plus recents sont rendus. */
  readonly conflictsTruncated: boolean;
}

export interface AdminRecordsQuery {
  readonly type: string;
  readonly page: number;
  readonly perPage: number;
  /** Instants ATOM a la seconde (`2026-10-01T00:00:00+02:00`) : les millisecondes sont refusees en 422. */
  readonly updatedFrom?: string;
  readonly updatedTo?: string;
}

// ---------------------------------------------------------------------------
// Demandes RGPD (registre manuel, BO-22)
// ---------------------------------------------------------------------------

export type PrivacyRequestType = 'access' | 'rectification' | 'erasure' | 'portability' | 'objection';

export type PrivacyRequestStatus = 'received' | 'in_progress' | 'closed';

/**
 * Demande d'exercice d'un droit. `overdue`, `daysRemaining` et `elapsedPercent`
 * sont **calcules par le serveur a la lecture** : l'ecran ne les recalcule pas,
 * il n'a ni la bonne horloge ni le bon fuseau (jours civils UTC).
 */
export interface AdminPrivacyRequest {
  readonly id: string;
  readonly type: PrivacyRequestType;
  readonly requesterEmail: string;
  /** Compte actif retrouve par l'adresse a la saisie ; `null` si aucun. */
  readonly userId: string | null;
  readonly receivedAt: string;
  readonly dueAt: string;
  readonly status: PrivacyRequestStatus;
  readonly assigneeUserId: string | null;
  /** Adresse actuelle du responsable ; `null` sans responsable ou si son compte a disparu. */
  readonly assigneeEmail: string | null;
  readonly outcomeNote: string | null;
  readonly closedAt: string | null;
  readonly overdue: boolean;
  /** Jours civils jusqu'a l'echeance, negatif si depassee ; `null` si close. */
  readonly daysRemaining: number | null;
  readonly elapsedPercent: number | null;
  /** `null` tant que la demande est ouverte. */
  readonly closedOnTime: boolean | null;
}

export interface AdminPrivacyRequestSummary {
  readonly open: number;
  readonly overdue: number;
  readonly openByType: Readonly<Record<PrivacyRequestType, number>>;
  readonly nearestDueAt: string | null;
  readonly nearestDueDaysRemaining: number | null;
  readonly closed90d: number;
  readonly closed90dOnTime: number;
  /** `null` sans aucune cloture sur 90 jours : jamais zero. */
  readonly meanDelayDays90d: number | null;
  /** Effacements executes sur-le-champ par l'application : hors registre. */
  readonly automaticErasures90d: number;
}

export interface NewPrivacyRequestInput {
  readonly type: PrivacyRequestType;
  readonly requesterEmail: string;
  /** `AAAA-MM-JJ` ou instant ISO 8601 avec fuseau. */
  readonly receivedAt: string;
}

/** Seuls les champs presents changent ; `null` retire le responsable ou vide la note. */
export interface PrivacyRequestPatch {
  readonly status?: PrivacyRequestStatus;
  readonly assigneeUserId?: string | null;
  readonly outcomeNote?: string | null;
}

export interface AdminPrivacyRequestFilters {
  readonly status?: PrivacyRequestStatus;
  readonly type?: PrivacyRequestType;
}

/** Registre des acces de l'administration (`GET /admin/audit-events/access-summary`, BO-08). */
export interface AdminAccessSummary {
  readonly days: number;
  readonly since: string;
  readonly items: readonly {
    /** `null` : la console, ou l'octroi de role se joue sans session. */
    readonly actorUserId: string | null;
    readonly actorEmail: string | null;
    readonly requestCount: number;
    readonly distinctAccountCount: number;
    readonly roleChanges: number;
  }[];
}

/** Export produit pour un tiers (`GET /admin/exports`). Ni contenu ni signature. */
export interface AdminExportFile {
  readonly id: string;
  readonly userId: string;
  readonly userEmail: string;
  readonly babyProfileId: string;
  readonly format: string;
  readonly filename: string;
  readonly sizeBytes: number;
  readonly createdAt: string;
  readonly expiresAt: string;
  /** Hors fenetre de validite : la ligne existe encore, elle n'est plus servie. */
  readonly expired: boolean;
}

/**
 * Corps commun des actions sensibles (§4 du contrat d'implementation, ADR 0035).
 *
 * - `reasonCode` est pris dans la liste fermee de l'action (`sensitiveActions.ts`) ;
 * - `reason` precise le motif : facultatif, sauf pour `other` ;
 * - `confirmation` doit egaler exactement la valeur attendue — l'adresse de la
 *   cible, le nom du reglage. Une case a cocher ne suffit pas : le clic reflexe
 *   est le mode d'echec reel, et retaper une adresse oblige a lire laquelle ;
 * - `password` n'est envoye que pour une action lourde. Le serveur le verifie et
 *   ne le garde nulle part ; l'ecran non plus, au-dela de l'appel.
 */
export interface SensitiveActionInput {
  readonly reasonCode: string;
  readonly reason?: string;
  readonly confirmation: string;
  readonly password?: string;
}

// ---------------------------------------------------------------------------
// Lectures de la tracabilite du stock
// ---------------------------------------------------------------------------

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Recherche d'un lot : `{items}` de lots, sans enveloppe de pagination. */
const readLookup = (body: unknown): readonly StockLookupItem[] => {
  if (!isRecord(body) || !Array.isArray(body.items)) {
    throw new Error('Réponse sans liste de lots pour la recherche.');
  }

  return body.items as readonly StockLookupItem[];
};

/**
 * Valide les quatre blocs de la trace sans en supposer le detail : un corps qui
 * n'a pas cette forme doit produire une erreur lisible, pas un `.map` qui casse
 * trois composants plus loin.
 */
const readTrace = (body: unknown): StockTrace => {
  if (
    !isRecord(body) ||
    !isRecord(body.batch) ||
    typeof body.batch.id !== 'string' ||
    !Array.isArray(body.movements) ||
    !isRecord(body.lineage) ||
    !Array.isArray(body.lineage.parents) ||
    !Array.isArray(body.lineage.children) ||
    !Array.isArray(body.integrity)
  ) {
    throw new Error('Réponse inattendue du serveur pour la traçabilité du lot.');
  }

  return body as unknown as StockTrace;
};

const readIntegrityReport = (body: unknown): StockIntegrityReport => {
  if (!isRecord(body) || !Array.isArray(body.checks) || typeof body.batchCount !== 'number') {
    throw new Error('Réponse inattendue du serveur pour les contrôles d’intégrité.');
  }

  return body as unknown as StockIntegrityReport;
};

// ---------------------------------------------------------------------------
// Appels
// ---------------------------------------------------------------------------

const page = (pageNumber: number, perPage: number): Record<string, number> => ({
  page: pageNumber,
  perPage,
});

export const createAdminApi = (http: HttpClient) => ({
  identity: (): Promise<AdminIdentity> => http.request<AdminIdentity>({ path: '/admin/me' }),

  listUsers: async (
    search: string,
    pageNumber = 1,
    perPage = 25,
    filters: AdminUserFilters = {},
  ): Promise<AdminPage<AdminUserSummary>> =>
    readPage<AdminUserSummary>(
      await http.request({
        path: '/admin/users',
        query: {
          ...page(pageNumber, perPage),
          ...(search === '' ? {} : { search }),
          ...Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== undefined && value !== '')),
        },
      }),
      'comptes',
    ),

  /** Leve la suspension d'un compte (BO-10). Role `admin`, sans mot de passe. */
  reactivateUser: (userId: string, input: SensitiveActionInput): Promise<unknown> =>
    http.request({
      method: 'POST',
      path: `/admin/users/${encodeURIComponent(userId)}/reactivate`,
      body: input,
    }),

  listFamilies: async (
    query: AdminFamiliesQuery,
    pageNumber = 1,
    perPage = 25,
  ): Promise<AdminPage<AdminFamilySummary>> =>
    readPage<AdminFamilySummary>(
      await http.request({
        path: '/admin/babies',
        query: {
          ...page(pageNumber, perPage),
          search: query.search === '' ? undefined : query.search,
          state: query.state ?? undefined,
          hasPendingInvitation: query.pendingInvitation ? 'true' : undefined,
        },
      }),
      'familles',
    ),

  getFamily: (babyId: string): Promise<AdminFamilyDetail> =>
    http.request<AdminFamilyDetail>({ path: `/admin/babies/${encodeURIComponent(babyId)}` }),

  /** Lecture seule : le role s'octroie et se retire a la console (ADR 0032). */
  listAdmins: async (pageNumber = 1, perPage = 100): Promise<AdminPage<AdminHolder>> =>
    readPage<AdminHolder>(
      await http.request({ path: '/admin/admins', query: page(pageNumber, perPage) }),
      'administrateurs',
    ),

  getUser: (userId: string): Promise<AdminUserDetail> =>
    http.request<AdminUserDetail>({ path: `/admin/users/${encodeURIComponent(userId)}` }),

  listUserSessions: async (userId: string): Promise<AdminPage<AdminUserSession>> =>
    readPage<AdminUserSession>(
      await http.request({ path: `/admin/users/${encodeURIComponent(userId)}/sessions` }),
      'sessions du compte',
    ),

  /** Registre des conflits, role `support` : filtres et pagination au serveur, compteurs du serveur. */
  listSyncConflicts: async (
    filters: Readonly<Record<string, string>>,
    pageNumber = 1,
    perPage = 25,
  ): Promise<AdminSyncConflictPage> => {
    const body = await http.request({
      path: '/admin/synchronization-conflicts',
      query: { ...page(pageNumber, perPage), ...filters },
    });

    return {
      ...readPage<AdminSyncConflict>(body, 'conflits de synchronisation'),
      counts: readConflictCounts((body as { counts?: unknown }).counts),
    };
  },

  getSyncConflict: (conflictId: string): Promise<AdminSyncConflictDetail> =>
    http.request<AdminSyncConflictDetail>({ path: `/admin/synchronization-conflicts/${encodeURIComponent(conflictId)}` }),

  getUserActivity: (userId: string, days: number): Promise<AdminUserActivity> =>
    http.request<AdminUserActivity>({
      path: `/admin/users/${encodeURIComponent(userId)}/activity`,
      query: { days },
    }),

  suspendUser: (userId: string, input: SensitiveActionInput): Promise<unknown> =>
    http.request({
      method: 'POST',
      path: `/admin/users/${encodeURIComponent(userId)}/suspend`,
      body: input,
    }),

  revokeSessions: (userId: string, input: SensitiveActionInput): Promise<unknown> =>
    http.request({
      method: 'POST',
      path: `/admin/users/${encodeURIComponent(userId)}/revoke-sessions`,
      body: input,
    }),

  anonymizeUser: (userId: string, input: SensitiveActionInput): Promise<unknown> =>
    http.request({
      method: 'POST',
      path: `/admin/users/${encodeURIComponent(userId)}/anonymize`,
      body: input,
    }),

  /**
   * Declenche un export POUR UN TIERS.
   *
   * `babyProfileId` est obligatoire, et ce n'est pas un detail de signature :
   * le service d'export du projet n'exporte que par profil bebe, et l'appeler
   * sans lui rend `422 validation_failed`. L'ecran doit donc faire CHOISIR le
   * profil — exporter d'office tous ceux de la cible sur-extrairait, ce qu'une
   * demande de droit d'acces ne justifie pas.
   *
   * L'export est produit au nom du compte vise, pas de l'administrateur : c'est
   * ce qui fait qu'il est refuse si cette personne n'a pas elle-meme le droit
   * d'exporter ce profil, et qu'il est purge avec son compte.
   */
  requestExport: (
    userId: string,
    input: SensitiveActionInput & { readonly babyProfileId: string },
  ): Promise<unknown> =>
    http.request({
      method: 'POST',
      path: `/admin/users/${encodeURIComponent(userId)}/exports`,
      body: input,
    }),

  listAuditEvents: async (
    filters: Readonly<Record<string, string>>,
    pageNumber = 1,
    perPage = 50,
  ): Promise<AdminPage<AdminAuditEvent>> =>
    readPage<AdminAuditEvent>(
      await http.request({
        path: '/admin/audit-events',
        query: { ...page(pageNumber, perPage), ...filters },
      }),
      'journal d’audit',
    ),

  /** Registre des acces sur `days` jours (7 par defaut cote serveur). Role `support`. */
  accessSummary: (days?: number): Promise<AdminAccessSummary> =>
    http.request<AdminAccessSummary>({ path: '/admin/audit-events/access-summary', query: { days } }),

  /** Exports produits, le plus recent d'abord. Role `support` ; le telechargement exige `admin`. */
  listExports: async (pageNumber = 1, perPage = 5): Promise<AdminPage<AdminExportFile>> =>
    readPage<AdminExportFile>(
      await http.request({ path: '/admin/exports', query: page(pageNumber, perPage) }),
      'exports produits',
    ),

  /** Telechargement **audite** (`admin`) : le fichier traverse le client authentifie, pas un lien. */
  downloadExport: (exportId: string): Promise<DownloadedFile> =>
    http.request<DownloadedFile>({ path: `/admin/exports/${encodeURIComponent(exportId)}/download`, asFile: true }),

  listPrivacyRequests: async (
    filters: AdminPrivacyRequestFilters,
    pageNumber = 1,
    perPage = 25,
  ): Promise<AdminPage<AdminPrivacyRequest>> =>
    readPage<AdminPrivacyRequest>(
      await http.request({
        path: '/admin/privacy-requests',
        query: { ...page(pageNumber, perPage), status: filters.status, type: filters.type },
      }),
      'demandes RGPD',
    ),

  privacyRequestsSummary: (): Promise<AdminPrivacyRequestSummary> =>
    http.request<AdminPrivacyRequestSummary>({ path: '/admin/privacy-requests/summary' }),

  createPrivacyRequest: (input: NewPrivacyRequestInput): Promise<AdminPrivacyRequest> =>
    http.request<AdminPrivacyRequest>({ method: 'POST', path: '/admin/privacy-requests', body: input }),

  updatePrivacyRequest: (id: string, patch: PrivacyRequestPatch): Promise<AdminPrivacyRequest> =>
    http.request<AdminPrivacyRequest>({
      method: 'PATCH',
      path: `/admin/privacy-requests/${encodeURIComponent(id)}`,
      body: patch,
    }),

  metrics: (period?: MetricsPeriodKey): Promise<AdminMetrics> =>
    http.request<AdminMetrics>({ path: '/admin/metrics', query: { period } }),

  /** `period` est toujours envoye : l'URL de l'ecran dit la periode, pas un defaut du serveur. */
  statistics: (period: StatisticsPeriodKey): Promise<AdminStatistics> =>
    http.request<AdminStatistics>({ path: '/admin/statistics', query: { period } }),

  attention: (): Promise<AdminAttention> => http.request<AdminAttention>({ path: '/admin/attention' }),

  /**
   * Export CSV du journal, memes filtres que la liste et **sans pagination**.
   * Role `admin` ; le serveur ecrit `admin.audit.exported` avant de repondre, et
   * pose `X-Truncated: true` au-dela de 10 000 lignes.
   */
  exportAuditEvents: (filters: Readonly<Record<string, string>>): Promise<DownloadedFile> =>
    http.request<DownloadedFile>({ path: '/admin/audit-events/export', query: filters, asFile: true }),

  listCollections: async (babyId: string): Promise<readonly AdminCollectionSummary[]> => {
    const body = await http.request<{ items?: unknown }>({
      path: `/admin/babies/${encodeURIComponent(babyId)}/collections`,
    });

    if (!Array.isArray(body.items)) {
      throw new Error('Réponse sans liste de collections.');
    }

    return (body.items as readonly AdminCollectionSummary[]).filter(
      (item) => typeof item.type === 'string' && typeof item.count === 'number',
    );
  },

  listRecords: async (babyId: string, query: AdminRecordsQuery): Promise<AdminPage<AdminDataRecord>> =>
    readPage<AdminDataRecord>(
      await http.request({
        path: `/admin/babies/${encodeURIComponent(babyId)}/records`,
        query: {
          type: query.type,
          ...page(query.page, query.perPage),
          updatedFrom: query.updatedFrom,
          updatedTo: query.updatedTo,
        },
      }),
      'enregistrements',
    ),

  getRecordHistory: async (
    babyId: string,
    type: string,
    entityId: string,
    pageNumber = 1,
    perPage = 100,
  ): Promise<AdminRecordHistory> => {
    const body = await http.request<Record<string, unknown>>({
      path: `/admin/babies/${encodeURIComponent(babyId)}/records/${encodeURIComponent(type)}/${encodeURIComponent(entityId)}/versions`,
      query: page(pageNumber, perPage),
    });

    return {
      ...readPage<AdminRecordVersion>(body, 'versions'),
      conflicts: Array.isArray(body.conflicts) ? (body.conflicts as readonly AdminVersionConflict[]) : [],
      conflictsTruncated: body.conflictsTruncated === true,
    };
  },

  health: (): Promise<AdminHealth> => http.request<AdminHealth>({ path: '/admin/health' }),

  listFailedMessages: async (pageNumber = 1, perPage = 25): Promise<AdminPage<AdminFailedMessage>> =>
    readPage<AdminFailedMessage>(
      await http.request({ path: '/admin/failed-messages', query: page(pageNumber, perPage) }),
      'traitements en erreur',
    ),

  retryFailedMessage: (id: string, input: SensitiveActionInput): Promise<unknown> =>
    http.request({
      method: 'POST',
      path: `/admin/failed-messages/${encodeURIComponent(id)}/retry`,
      body: input,
    }),

  listLegalDocuments: async (): Promise<readonly AdminLegalDocument[]> => {
    const body = await http.request<{ items?: unknown }>({ path: '/admin/legal-documents' });

    if (!Array.isArray(body.items)) {
      throw new Error('Réponse sans liste de documents.');
    }

    return body.items as readonly AdminLegalDocument[];
  },

  listLegalVersions: async (
    type: AdminLegalDocumentType,
    pageNumber = 1,
    perPage = 10,
  ): Promise<AdminPage<AdminLegalVersionSummary>> =>
    readPage<AdminLegalVersionSummary>(
      await http.request({ path: `/admin/legal-documents/${type}/versions`, query: page(pageNumber, perPage) }),
      'versions',
    ),

  /** Une version publiee par son numero, ou le brouillon (`'draft'`). */
  getLegalVersion: (type: AdminLegalDocumentType, number: number | 'draft'): Promise<AdminLegalVersion> =>
    http.request<AdminLegalVersion>({ path: `/admin/legal-documents/${type}/versions/${String(number)}` }),

  /** `expectedRevision` : la revision lue, `0` si le client n'a vu aucun brouillon. */
  saveLegalDraft: (
    type: AdminLegalDocumentType,
    content: string,
    expectedRevision: number,
  ): Promise<AdminLegalVersion> =>
    http.request<AdminLegalVersion>({
      method: 'PUT',
      path: `/admin/legal-documents/${type}/draft`,
      body: { content, expectedRevision },
    }),

  publishLegalDraft: (
    type: AdminLegalDocumentType,
    expectedRevision: number,
    input: SensitiveActionInput,
  ): Promise<AdminLegalVersion> =>
    http.request<AdminLegalVersion>({
      method: 'POST',
      path: `/admin/legal-documents/${type}/draft/publish`,
      body: { ...input, expectedRevision },
    }),

  listSettings: (): Promise<{ items: readonly AdminSetting[] }> =>
    http.request<{ items: readonly AdminSetting[] }>({ path: '/admin/settings' }),

  maintenance: (): Promise<AdminMaintenance> => http.request<AdminMaintenance>({ path: '/admin/maintenance' }),

  /**
   * Ecrit plusieurs reglages d'une meme famille en une action sensible : un
   * motif, une confirmation (`maintenance`), un mot de passe pour les actions
   * lourdes. Rend les reglages ecrits, tels que le serveur les a enregistres.
   */
  updateSettings: (
    values: Readonly<Record<string, boolean | string>>,
    input: SensitiveActionInput,
  ): Promise<{ items: readonly AdminSetting[] }> =>
    http.request<{ items: readonly AdminSetting[] }>({
      method: 'PATCH',
      path: '/admin/settings',
      body: { values, ...input },
    }),

  /**
   * Retrouve un lot par son code, ou par l'identifiant d'un lot, d'un tirage ou
   * d'un biberon. Chaque appel ecrit une ligne d'audit : l'ecran ne le lance
   * que sur un geste explicite.
   */
  lookupStockBatches: async (query: string): Promise<readonly StockLookupItem[]> =>
    readLookup(await http.request({ path: '/admin/stock/lookup', query: { q: query } })),

  /**
   * Trace d'un lot (etat, mouvements, genealogie, controles). `scopeId` est le
   * `babyId` rendu par la recherche : le perimetre du journal, a reprendre tel
   * quel.
   */
  getStockTrace: async (scopeId: string, batchId: string): Promise<StockTrace> =>
    readTrace(
      await http.request({
        path: `/admin/babies/${encodeURIComponent(scopeId)}/stock/batches/${encodeURIComponent(batchId)}/trace`,
      }),
    ),

  /**
   * Les sept controles sur tous les lots d'un perimetre. A la demande seulement :
   * le serveur rejoue tout le journal du perimetre et ne garde aucun resultat.
   */
  getStockIntegrity: async (scopeId: string): Promise<StockIntegrityReport> =>
    readIntegrityReport(
      await http.request({ path: `/admin/babies/${encodeURIComponent(scopeId)}/stock/integrity` }),
    ),
});

export type AdminApi = ReturnType<typeof createAdminApi>;
